import  { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAppSelector } from "@/store/store";
import { BranchService } from "@/services/Branch/BranchSevice";
import { SiteService } from "@/services/Site/SiteServive";
import { DepartmentService } from "@/services/Department/Department";
import { DesignationService } from "@/services/Designation/DesignationService";
import ShiftAndTimeService from "@/services/ShiftAndTime/ShiftAndTime";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";

import SearchSelect, { type Option } from "@/components/common/SearchSelect";
import { ThrowError } from "@/api/callApi";

// ── Supported Filter Types ──────────────────────────────────────────────────
export type AppFilterType =
  | "branch"
  | "site"
  | "department"
  | "designation"
  | "shift"
  | "employee";

// ── Filter Values State ─────────────────────────────────────────────────────
export interface AppFilterValues {
  branchId: string;
  siteId: string;
  departmentId: string;
  designationId: string;
  shiftId: string;
  employeeId: string;
}

// ── Filter Value Key Mapping ────────────────────────────────────────────────
const FILTER_VALUE_KEY: Record<AppFilterType, keyof AppFilterValues> = {
  branch: "branchId",
  site: "siteId",
  department: "departmentId",
  designation: "designationId",
  shift: "shiftId",
  employee: "employeeId",
};

// ── Individual Filter Config ────────────────────────────────────────────────
// Each independent filter (not branch/site) is defined here.
// To add a new filter: add entry here + add to AppFilterType + add to AppFilterValues + add to FILTER_VALUE_KEY
interface IndependentFilterConfig {
  service: (params: string) => Promise<any>;
  dataKey: string;
  labelKey: string;
  placeholder: string;
}

const INDEPENDENT_FILTERS: Record<string, IndependentFilterConfig> = {
  department: {
    service: DepartmentService.getDepartment,
    dataKey: "department",
    labelKey: "name",
    placeholder: "Select Department...",
  },
  designation: {
    service: DesignationService.getDesignation,
    dataKey: "designation",
    labelKey: "name",
    placeholder: "Select Designation...",
  },
  shift: {
    service: ShiftAndTimeService.getShiftAndTime,
    dataKey: "shift",
    labelKey: "shiftName",
    placeholder: "Select Shift...",
  },
  employee: {
    service: EmployeeOnBoardService.getAllEmployees,
    dataKey: "user",
    labelKey: "name",
    placeholder: "Select Employee...",
  },
};

// ── Hook Options ────────────────────────────────────────────────────────────
export interface UseAppFiltersOptions {
  /** Which filters to activate */
  enabled: AppFilterType[];
  /** Initial values for filters */
  initialValues?: Partial<AppFilterValues>;
  /** Auto select first item when loaded */
  autoSelectFirst?: Partial<Record<AppFilterType, boolean>>;
  /** Allow clearing selection (default: true) */
  allowClear?: boolean;
  /** Custom placeholders */
  placeholders?: Partial<Record<AppFilterType, string>>;
  /** Custom class for each dropdown wrapper */
  itemClassName?: string;
  /** Custom class for the combined container */
  containerClassName?: string;
  /** Callback fired whenever any filter changes */
  onChange?: (values: AppFilterValues, changedKey?: AppFilterType) => void;
}

// ── Empty Filter Values (Reusable constant) ─────────────────────────────────
const EMPTY_FILTER_VALUES: AppFilterValues = {
  branchId: "",
  siteId: "",
  departmentId: "",
  designationId: "",
  shiftId: "",
  employeeId: "",
};

// ═════════════════════════════════════════════════════════════════════════════
// ██  CORE HOOK
// ═════════════════════════════════════════════════════════════════════════════
export const useAppFilters = (options: UseAppFiltersOptions) => {
  const {
    enabled = [],
    initialValues = {},
    autoSelectFirst = {},
    allowClear = true,
    placeholders = {},
    itemClassName = "w-full sm:w-52 min-w-[120px]",
    containerClassName = "flex items-center gap-4 flex-wrap",
    onChange,
  } = options;

  // ── Stable Refs ───────────────────────────────────────────────────────────
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const abortControllerRef = useRef<AbortController | null>(null);

  // ── Boolean Flags (Primitives — never cause re-render loops) ──────────────
  const isBranchEnabled = enabled.includes("branch");
  const isSiteEnabled = enabled.includes("site");
  const isDepartmentEnabled = enabled.includes("department");
  const isDesignationEnabled = enabled.includes("designation");
  const isShiftEnabled = enabled.includes("shift");
  const isEmployeeEnabled = enabled.includes("employee");

  // ── Auth User & HQ Check ──────────────────────────────────────────────────
  const user = useAppSelector((state) => state.user.user);
  const isHQ = useMemo(() => {
    if (!user) return false;
    return Number(user.designation?.level) === 0;
  }, [user]);

  // ── Filter Values State ───────────────────────────────────────────────────
  const [filterValues, setFilterValues] = useState<AppFilterValues>({
    ...EMPTY_FILTER_VALUES,
    ...initialValues,
  });

  // ── Raw Data State (branch & site separate, independent filters in a map) ─
  const [branches, setBranches] = useState<any[]>([]);
  const [allSites, setAllSites] = useState<any[]>([]);
  const [independentData, setIndependentData] = useState<Record<string, any[]>>({
    department: [],
    designation: [],
    shift: [],
    employee: [],
  });

  // ── Loading State ─────────────────────────────────────────────────────────
  const [loading, setLoading] = useState<Record<AppFilterType, boolean>>({
    branch: false, site: false, department: false,
    designation: false, shift: false, employee: false,
  });

  const setItemLoading = useCallback((key: AppFilterType, isLoading: boolean) => {
    setLoading((prev) => (prev[key] === isLoading ? prev : { ...prev, [key]: isLoading }));
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  FETCH: Branch & Site (Cascading — Special handling)
  // ═══════════════════════════════════════════════════════════════════════════
  const fetchBranchesAndSites = useCallback(async () => {
    if (!isHQ || (!isBranchEnabled && !isSiteEnabled)) return;

    setItemLoading("branch", true);
    setItemLoading("site", true);
    try {
      const [branchRes, siteRes] = await Promise.all([
        isBranchEnabled ? BranchService.getBranch("pageSize=100") : null,
        isSiteEnabled ? SiteService.getSite("pageSize=300") : null,
      ]);

      const bList = branchRes?.status && branchRes?.data?.branch ? branchRes.data.branch : [];
      const sList = siteRes?.status && siteRes?.data?.site ? siteRes.data.site : [];

      if (bList.length) setBranches(bList);
      if (sList.length) setAllSites(sList);

      // Auto-select first branch/site if configured
      setFilterValues((prev) => {
        let branchId = prev.branchId;
        let siteId = prev.siteId;

        if (!branchId && autoSelectFirst.branch && bList.length > 0) {
          branchId = bList[0]?.id || "";
        }
        if (!siteId && autoSelectFirst.site && sList.length > 0) {
          const matched = branchId
            ? sList.find((s: any) => s?.branchId === branchId || s?.branch?.id === branchId)
            : null;
          siteId = matched?.id || sList[0]?.id || "";
        }

        if (branchId === prev.branchId && siteId === prev.siteId) return prev;
        return { ...prev, branchId, siteId };
      });
    } catch (error) {
      ThrowError(error);
    } finally {
      setItemLoading("branch", false);
      setItemLoading("site", false);
    }
  }, [isHQ, isBranchEnabled, isSiteEnabled, autoSelectFirst.branch, autoSelectFirst.site, setItemLoading]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  FETCH: Generic Independent Filter Fetcher
  // ═══════════════════════════════════════════════════════════════════════════
  const createFetcher = useCallback(
    (filterType: AppFilterType) => {
      return async (search = "") => {
        const config = INDEPENDENT_FILTERS[filterType];
        if (!config) return;

        setItemLoading(filterType, true);
        try {
          const searchParam = search
            ? `search=${encodeURIComponent(search)}&pageSize=50`
            : "pageSize=100";
          const res = await config.service(searchParam);
          if (res?.status && res?.data?.[config.dataKey]) {
            setIndependentData((prev) => ({
              ...prev,
              [filterType]: res.data[config.dataKey],
            }));
          }
        } catch (error) {
          ThrowError(error);
        } finally {
          setItemLoading(filterType, false);
        }
      };
    },
    [setItemLoading]
  );

  // Stable fetch references for each independent filter
  const fetchDepartments = useCallback(
    (search = "") => isDepartmentEnabled ? createFetcher("department")(search) : Promise.resolve(),
    [isDepartmentEnabled, createFetcher]
  );
  const fetchDesignations = useCallback(
    (search = "") => isDesignationEnabled ? createFetcher("designation")(search) : Promise.resolve(),
    [isDesignationEnabled, createFetcher]
  );
  const fetchShifts = useCallback(
    (search = "") => isShiftEnabled ? createFetcher("shift")(search) : Promise.resolve(),
    [isShiftEnabled, createFetcher]
  );
  const fetchEmployees = useCallback(
    (search = "") => isEmployeeEnabled ? createFetcher("employee")(search) : Promise.resolve(),
    [isEmployeeEnabled, createFetcher]
  );


  // ═══════════════════════════════════════════════════════════════════════════
  // ██  INITIAL DATA LOAD (Mount only)
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (isHQ && (isBranchEnabled || isSiteEnabled)) fetchBranchesAndSites();
  }, [isHQ, isBranchEnabled, isSiteEnabled, fetchBranchesAndSites]);

  useEffect(() => { if (isDepartmentEnabled) fetchDepartments(""); }, [isDepartmentEnabled, fetchDepartments]);
  useEffect(() => { if (isDesignationEnabled) fetchDesignations(""); }, [isDesignationEnabled, fetchDesignations]);
  useEffect(() => { if (isShiftEnabled) fetchShifts(""); }, [isShiftEnabled, fetchShifts]);
  useEffect(() => { if (isEmployeeEnabled) fetchEmployees(""); }, [isEmployeeEnabled, fetchEmployees]);

  // ── Cleanup on Unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  CASCADING: Available Sites filtered by selected Branch
  // ═══════════════════════════════════════════════════════════════════════════
  const availableSites = useMemo(() => {
    if (!filterValues.branchId) return allSites;
    return allSites.filter(
      (s) => s.branchId === filterValues.branchId || s.branch?.id === filterValues.branchId
    );
  }, [allSites, filterValues.branchId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  CHANGE HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  const setFilterValue = useCallback(
    (key: AppFilterType, value: string) => {
      setFilterValues((prev) => {
        const next = { ...prev };
        const valueKey = FILTER_VALUE_KEY[key];
        next[valueKey] = value;

        // Cascade: Branch change → reset Site
        if (key === "branch") {
          const matched = allSites.filter(
            (s) => s.branchId === value || s.branch?.id === value
          );
          next.siteId = autoSelectFirst.site && matched.length > 0 ? matched[0]?.id || "" : "";
        }

        onChangeRef.current?.(next, key);
        return next;
      });
    },
    [allSites, autoSelectFirst.site]
  );

  const resetFilters = useCallback(() => {
    setFilterValues(EMPTY_FILTER_VALUES);
    onChangeRef.current?.(EMPTY_FILTER_VALUES);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  OPTIONS: Convert raw data → SearchSelect Option[]
  // ═══════════════════════════════════════════════════════════════════════════
  const toOptions = (items: any[], labelKey: string, isEmployee = false): Option[] =>
    items
      .filter((item) => item?.id)
      .map((item) => ({
        value: item.id as string,
        label: isEmployee
          ? `${item.name || "—"}${item.employeeCode ? ` (${item.employeeCode})` : ""}`
          : item[labelKey] || "—",
      }));

  const branchOptions = useMemo(() => toOptions(branches, "name"), [branches]);
  const siteOptions = useMemo(() => toOptions(availableSites, "name"), [availableSites]);

  const departmentOptions = useMemo(() => toOptions(independentData.department, "name"), [independentData.department]);
  const designationOptions = useMemo(() => toOptions(independentData.designation, "name"), [independentData.designation]);
  const shiftOptions = useMemo(() => toOptions(independentData.shift, "shiftName"), [independentData.shift]);
  const employeeOptions = useMemo(() => toOptions(independentData.employee, "name", true), [independentData.employee]);

  // All options in one object for convenience
  const allOptions = useMemo(() => ({
    branch: branchOptions,
    site: siteOptions,
    department: departmentOptions,
    designation: designationOptions,
    shift: shiftOptions,
    employee: employeeOptions,
  }), [branchOptions, siteOptions, departmentOptions, designationOptions, shiftOptions, employeeOptions]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  EFFECTIVE IDs (HQ vs Non-HQ)
  // ═══════════════════════════════════════════════════════════════════════════
  const effectiveBranchId = useMemo(() => {
    if (isHQ) return filterValues.branchId;
    return user?.branch?.id || user?.branchId || "";
  }, [isHQ, filterValues.branchId, user]);

  const effectiveSiteId = useMemo(() => {
    if (isHQ) return filterValues.siteId;
    return user?.site?.id || user?.siteId || "";
  }, [isHQ, filterValues.siteId, user]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  QUERY STRING BUILDER
  // ═══════════════════════════════════════════════════════════════════════════
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (isBranchEnabled && effectiveBranchId) params.branchId = effectiveBranchId;
    if (isSiteEnabled && effectiveSiteId) params.siteId = effectiveSiteId;
    if (isDepartmentEnabled && filterValues.departmentId) params.departmentId = filterValues.departmentId;
    if (isDesignationEnabled && filterValues.designationId) params.designationId = filterValues.designationId;
    if (isShiftEnabled && filterValues.shiftId) params.shiftId = filterValues.shiftId;
    if (isEmployeeEnabled && filterValues.employeeId) params.employeeId = filterValues.employeeId;
    return params;
  }, [isBranchEnabled, isSiteEnabled, isDepartmentEnabled, isDesignationEnabled, isShiftEnabled, isEmployeeEnabled, effectiveBranchId, effectiveSiteId, filterValues]);

  const queryString = useMemo(() => {
    return Object.entries(queryParams)
      .map(([key, val]) => `&${key}=${encodeURIComponent(val)}`)
      .join("");
  }, [queryParams]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  RENDER: Generic Filter Node Builder
  // ═══════════════════════════════════════════════════════════════════════════
  const renderFilterNode = useCallback(
    (
      filterType: AppFilterType,
      nodeOptions: Option[],
      filterValueKey: keyof AppFilterValues,
      placeholder: string,
      onSearch: (search: string) => void,
      extra?: { disabled?: boolean }
    ) => (
      <div className={itemClassName} key={`filter-${filterType}`}>
        <SearchSelect
          placeholder={placeholders[filterType] || placeholder}
          options={nodeOptions}
          value={filterValues[filterValueKey]}
          onChange={(val) => setFilterValue(filterType, val)}
          onSearch={onSearch}
          isLoading={loading[filterType]}
          allowClear={allowClear}
          disabled={extra?.disabled}
          className="w-full"
        />
      </div>
    ),
    [itemClassName, placeholders, filterValues, setFilterValue, loading, allowClear]
  );

  // ── Branch Node (Special: HQ-only + inline search) ───────────────────────
  const branchSelectNode = useMemo(() => {
    if (!isBranchEnabled || !isHQ) return null;
    return renderFilterNode("branch", branchOptions, "branchId", "Select Branch...", (search) => {
      setItemLoading("branch", true);
      BranchService.getBranch(`pageSize=50${search ? `&search=${encodeURIComponent(search)}` : ""}`)
        .then((res) => res?.data?.branch && setBranches(res.data.branch))
        .finally(() => setItemLoading("branch", false));
    });
  }, [isBranchEnabled, isHQ, branchOptions, renderFilterNode, setItemLoading]);

  // ── Site Node (Special: HQ-only + cascading from branch) ──────────────────
  const siteSelectNode = useMemo(() => {
    if (!isSiteEnabled || !isHQ) return null;
    return renderFilterNode("site", siteOptions, "siteId", "Select Site...", (search) => {
      setItemLoading("site", true);
      const branchParam = filterValues.branchId ? `&branchId=${filterValues.branchId}` : "";
      SiteService.getSite(`pageSize=100${branchParam}${search ? `&search=${encodeURIComponent(search)}` : ""}`)
        .then((res) => res?.data?.site && setAllSites(res.data.site))
        .finally(() => setItemLoading("site", false));
    }, { disabled: isBranchEnabled && !filterValues.branchId });
  }, [isSiteEnabled, isHQ, siteOptions, filterValues.branchId, isBranchEnabled, renderFilterNode, setItemLoading]);

  // ── Independent Filter Nodes (Generic — config driven) ────────────────────
  const departmentSelectNode = useMemo(() => {
    if (!isDepartmentEnabled) return null;
    return renderFilterNode("department", departmentOptions, "departmentId", INDEPENDENT_FILTERS.department.placeholder, fetchDepartments);
  }, [isDepartmentEnabled, departmentOptions, renderFilterNode, fetchDepartments]);

  const designationSelectNode = useMemo(() => {
    if (!isDesignationEnabled) return null;
    return renderFilterNode("designation", designationOptions, "designationId", INDEPENDENT_FILTERS.designation.placeholder, fetchDesignations);
  }, [isDesignationEnabled, designationOptions, renderFilterNode, fetchDesignations]);

  const shiftSelectNode = useMemo(() => {
    if (!isShiftEnabled) return null;
    return renderFilterNode("shift", shiftOptions, "shiftId", INDEPENDENT_FILTERS.shift.placeholder, fetchShifts);
  }, [isShiftEnabled, shiftOptions, renderFilterNode, fetchShifts]);

  const employeeSelectNode = useMemo(() => {
    if (!isEmployeeEnabled) return null;
    return renderFilterNode("employee", employeeOptions, "employeeId", INDEPENDENT_FILTERS.employee.placeholder, fetchEmployees);
  }, [isEmployeeEnabled, employeeOptions, renderFilterNode, fetchEmployees]);

  // ── Combined Selectors Node ───────────────────────────────────────────────
  const filterSelectorsNode = useMemo(() => (
    <div className={containerClassName}>
      {branchSelectNode}
      {siteSelectNode}
      {departmentSelectNode}
      {designationSelectNode}
      {shiftSelectNode}
      {employeeSelectNode}
    </div>
  ), [containerClassName, branchSelectNode, siteSelectNode, departmentSelectNode, designationSelectNode, shiftSelectNode, employeeSelectNode]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ██  RETURN (Consumer API — unchanged)
  // ═══════════════════════════════════════════════════════════════════════════
  return {
    // Current Filter Values
    filterValues,
    setFilterValue,
    resetFilters,

    // Effective HQ/Non-HQ IDs
    isHQ,
    effectiveBranchId,
    effectiveSiteId,

    // API Query Helpers
    queryParams,
    queryString,

    // Options for all filters
    options: allOptions,

    // Loading indicators
    loading,

    // Individual Dropdown Nodes (for custom layout)
    branchSelectNode,
    siteSelectNode,
    departmentSelectNode,
    designationSelectNode,
    shiftSelectNode,
    employeeSelectNode,

    // Combined Plug-and-Play Node (for 1-line render in additionalFilters)
    filterSelectorsNode,
  };
};

export default useAppFilters;
